const FALLBACK_VERSION = '0.1.0-dev';
const FALLBACK_RELEASE_STATUS = 'dev';
const VERSION_FILE_URL = './VERSION';
const RELEASE_STATUS_FILE_URL = './RELEASE_STATUS';
const RELEASE_STATUS_LABELS = new Map([
  ['dev', 'dev'],
  ['unstable', 'instabil'],
  ['rc', 'rc'],
  ['stable', 'stabil']
]);

function normalizeVersion(value) {
  const version = String(value || '').trim();
  return version || FALLBACK_VERSION;
}

function displayVersion(version) {
  const normalized = normalizeVersion(version);
  return normalized.startsWith('v') ? normalized : `v${normalized}`;
}

function normalizeReleaseStatus(value) {
  const status = String(value || '').trim().toLowerCase();
  return RELEASE_STATUS_LABELS.has(status) ? status : FALLBACK_RELEASE_STATUS;
}

function displayReleaseStatus(status) {
  return RELEASE_STATUS_LABELS.get(normalizeReleaseStatus(status)) || FALLBACK_RELEASE_STATUS;
}

async function readAppVersion() {
  const response = await fetch(VERSION_FILE_URL, { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`VERSION konnte nicht geladen werden (${response.status}).`);
  }
  return normalizeVersion(await response.text());
}

async function readReleaseStatus() {
  const response = await fetch(RELEASE_STATUS_FILE_URL, { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`RELEASE_STATUS konnte nicht geladen werden (${response.status}).`);
  }
  return normalizeReleaseStatus(await response.text());
}

async function readAppReleaseMeta() {
  const [versionResult, statusResult] = await Promise.allSettled([
    readAppVersion(),
    readReleaseStatus()
  ]);
  if (versionResult.status === 'rejected') {
    throw versionResult.reason;
  }
  return {
    version: versionResult.value,
    releaseStatus: statusResult.status === 'fulfilled'
      ? statusResult.value
      : FALLBACK_RELEASE_STATUS
  };
}

function setVersionBadge(version, options = {}) {
  const badges = Array.from(document.querySelectorAll('[data-app-version]'));
  if (!badges.length) return;
  const releaseStatus = normalizeReleaseStatus(options.releaseStatus);
  const displayed = displayVersion(version);
  const statusLabel = displayReleaseStatus(releaseStatus);
  const displayedWithStatus = releaseStatus === 'stable'
    ? displayed
    : `${displayed} ${statusLabel}`;
  for (const badge of badges) {
    badge.textContent = displayedWithStatus;
    badge.title = options.fallback
      ? `KS Studio Version unbekannt, Fallback ${displayedWithStatus}`
      : `KS Studio ${displayed} (${statusLabel})`;
    badge.dataset.version = normalizeVersion(version);
    badge.dataset.releaseStatus = releaseStatus;
    badge.dataset.state = options.fallback ? 'fallback' : 'ready';
  }
  document.documentElement.dataset.ksStudioVersion = normalizeVersion(version);
  document.documentElement.dataset.ksStudioReleaseStatus = releaseStatus;
}

function initAppVersionBadge() {
  setVersionBadge(FALLBACK_VERSION, {
    fallback: true,
    releaseStatus: FALLBACK_RELEASE_STATUS
  });
  readAppReleaseMeta()
    .then(meta => setVersionBadge(meta.version, { releaseStatus: meta.releaseStatus }))
    .catch(error => {
      console.warn('KS Studio version could not be loaded', error);
      setVersionBadge(FALLBACK_VERSION, {
        fallback: true,
        releaseStatus: FALLBACK_RELEASE_STATUS
      });
    });
}

export {
  FALLBACK_RELEASE_STATUS,
  FALLBACK_VERSION,
  displayReleaseStatus,
  displayVersion,
  initAppVersionBadge,
  readAppReleaseMeta,
  readAppVersion,
  readReleaseStatus
};
